import { SmartImage } from "@/components/site/SmartImage";
import { knowledgeImages } from "@/lib/responsive-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Lock } from "lucide-react";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { seoLinks, ogImage } from "@/lib/seo";
import { resources } from "@/lib/resources";
import { ResourceWelcomeDialog } from "@/components/site/ResourceWelcomeDialog";

const title = "Knowledge hub for families moving to Spain";
const description =
  "In-depth guides, checklists and contact lists for international families on the Costa del Sol. Enter your email to unlock the full material, free of charge.";

export const Route = createFileRoute("/en/knowledge-hub/")({
  validateSearch: (search: Record<string, unknown>): { welcome?: true } =>
    search["welcome"] === 1 || search["welcome"] === "1" ? { welcome: true } : {},
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/knowledge-hub"),
  }),
  component: Page,
});

function Page() {
  const { welcome } = Route.useSearch();
  return (
    <>
      <ResourceWelcomeDialog locale="en" show={welcome === true} />
      <PageHero
        eyebrow="Knowledge hub"
        title="Everything we know about moving here, in one place"
        intro="We get the same questions every term: in what order should things be done, which documents are required, what does daily life cost and which numbers should be in your phone? Here are the answers, in four in-depth resources. Unlock all of them with your email address."
      />

      <section className="mx-auto max-w-5xl px-5 py-12 sm:py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          {resources.map((r) => (
            <Link
              key={r.slug}
              to="/en/knowledge-hub/$slug"
              params={{ slug: r.slugEn }}
              className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-lg"
            >
              <SmartImage
                image={knowledgeImages[r.slug]!}
                alt={r.titleEn}
                width={1280}
                height={720}
                sizes="(max-width: 640px) 100vw, 480px"
                className="aspect-[16/9] w-full bg-muted object-cover"
              />
              <div className="flex flex-1 flex-col p-6">
                <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-primary uppercase">
                  <Lock className="size-3.5" aria-hidden />
                  {r.minutes} min read
                </p>
                <h2 className="mt-3 text-xl leading-snug font-semibold">{r.titleEn}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {r.introEn}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                  Open the resource
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Prose>
        <h2>Why we collect this</h2>
        <p>
          Moving to Spain is hundreds of small decisions, and most of them are manageable – if you
          know the order to do them in. As a school we meet families at every stage of the move:
          those still considering it, those who have just signed a rental contract and those who
          have lived here for years. The knowledge hub is our way of gathering what families tell
          us, and what we see working, so the next family does not have to start from scratch.
        </p>
        <p>
          The material is practical rather than inspirational. We make no promises about processing
          times or decisions by the authorities – rules change and every family's situation is
          different. What we can describe is how the steps connect, what is usually required and
          where things tend to stall, so you can plan with sensible margins.
        </p>

        <h2>Why do you ask for an email address?</h2>
        <p>
          For two reasons. We want to be able to update the material when rules and contact details
          change and reach the families already using it, and it shows us which questions matter
          most so we can go deeper where it counts. You do not have to subscribe to the newsletter
          to unlock the material, and you can unsubscribe at any time. We never pass your address on.
        </p>

        <h2>Questions this material does not cover</h2>
        <p>
          Tax matters, residence permits for non-EU citizens and legal property questions should
          always be checked with a local gestor, lawyer or accountant. What we can answer is how
          schooling works, what the school year looks like and how families usually plan the move
          around the start of term. Get in touch and we will talk it through based on your situation.
        </p>
      </Prose>

      <CtaBand locale="en" />
      <div className="h-16" />
    </>
  );
}
